import { useEffect, useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { KyrgyzstanDetailContent } from 'widgets/kyrgyzstan/detail-content'
import { KyrgyzstanDetailShell } from 'widgets/kyrgyzstan/detail-shell'

import {
  getKyrgyzstanMapRouteState,
  useKyrgyzstanOrderDetail,
} from 'entities/kyrgyzstan/lib'
import { getDestinationTitle } from 'entities/kyrgyzstan/lib/get-destination-title'
import { getKyrgyzstanDetailSubtitle } from 'entities/kyrgyzstan/lib/get-kyrgyzstan-detail-subtitle'
import {
  isBookingCancellable,
  kyrgyzstanDetailDataStore,
} from 'entities/kyrgyzstan/model'
import { getShareUrl } from 'entities/tour/ui/appeal-details/libs/getShareUrl'

import { previewPdfUrl } from 'shared/lib/pdf-preview'
import { Button } from 'shared/ui/button'

import s from './styles.module.scss'

const KyrgyzstanOrderDetailPage = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { orderId } = useParams<{ orderId: string }>()

  const {
    data: order,
    isLoading: isLoadingOrder,
    error: orderError,
  } = useKyrgyzstanOrderDetail(orderId)

  const realtyId = order?.realty_object.parent_id || order?.realty_object.id
  const selectedRealtyChildrenId = order?.realty_object.id

  const {
    childrenList,
    parentDetail,
    fetchParentDetail,
    fetchChildrenList,
    resetParent,
    resetChildrenList,
    isParentLoading,
    isChildrenListLoading,
    parentError,
    childrenListError,
  } = kyrgyzstanDetailDataStore

  const realtyData = parentDetail?.id === realtyId ? parentDetail : null
  const hasChildren = Boolean(realtyData?.type.has_children)

  useEffect(() => {
    if (!realtyId) {
      resetParent()
      resetChildrenList()

      return
    }

    fetchParentDetail(realtyId)
  }, [realtyId, fetchParentDetail, resetParent, resetChildrenList])

  useEffect(() => {
    if (!realtyId || !order?.id || !hasChildren) {
      resetChildrenList()

      return undefined
    }

    fetchChildrenList(realtyId, { order: order.id })

    return resetChildrenList
  }, [realtyId, order?.id, hasChildren, fetchChildrenList, resetChildrenList])

  const selectedChildren = useMemo(() => {
    const selectedChild = childrenList.find(
      (item) => item.id === selectedRealtyChildrenId,
    )

    return selectedChild
      ? [
          {
            ...selectedChild,
            isSelected: true,
          },
        ]
      : []
  }, [childrenList, selectedRealtyChildrenId])

  const orderRealtyData = useMemo(() => {
    if (!order || !realtyData) {
      return null
    }

    return {
      ...realtyData,
      times: {
        ...realtyData.times,
        arrival_time: order.check_in_time,
        departure_time: order.check_out_time,
      },
      timezone: order.timezone,
    }
  }, [order, realtyData])

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleOpenFullMap = (parentId: string) => {
    if (!orderRealtyData || orderRealtyData.id !== parentId) return

    navigate(RoutingPaths.kyrgyzstan.map.path(parentId), {
      state: getKyrgyzstanMapRouteState(orderRealtyData),
    })
  }

  const handleOpenGallery = (parentId: string) => {
    if (!orderId || !realtyData || realtyData.id !== parentId) return

    navigate(RoutingPaths.kyrgyzstan.myOrders.gallery.path(orderId), {
      state: { realty: realtyData },
    })
  }

  const handleOpenVoucher = () => {
    previewPdfUrl(order?.file_url)
  }

  const handleCancelBooking = () => {
    if (!orderId) return

    // const message = t('kyrgyzstan_block.booking_card.cancel_booking_whatsapp', {
    //   orderId,
    // })

    // // TODO: Временно редирект на WhatsApp для отмены бронирования
    // openWhatsappWithMessage(message)

    navigate(RoutingPaths.kyrgyzstan.myOrders.cancelBooking.path(orderId))
  }

  const canCancel = isBookingCancellable(order?.status)
  const shareUrl = realtyId
    ? getShareUrl(RoutingPaths.kyrgyzstan.detail.path(realtyId))
    : undefined
  const detailSubtitle = getKyrgyzstanDetailSubtitle({
    searchParams: {
      start_date: order?.start_date,
      end_date: order?.end_date,
      visitors_count: order?.visitors_count,
      children_ages: order?.children_ages,
    },
    t,
  })
  const requestError = orderError || parentError || childrenListError
  const detailError =
    requestError ||
    (!order || !realtyData ? t('kyrgyzstan_block.error.title') : null)
  const isLoading = isLoadingOrder || isParentLoading

  return (
    <KyrgyzstanDetailShell
      header={{
        title: realtyData ? getDestinationTitle(realtyData) : undefined,
        subtitle: detailSubtitle,
        onGoBack: handleGoBack,
      }}
      share={
        realtyData && shareUrl
          ? {
              url: shareUrl,
              text: realtyData.title,
            }
          : undefined
      }
      state={{
        isLoading,
        error: detailError,
      }}
      bottomAction={
        canCancel && (
          <div className={clsx(s.buttonsContainer, 'container')}>
            <Button
              variant="dangerOutlined"
              onClick={handleCancelBooking}
            >
              <span className="semi-bold-17">
                {t('kyrgyzstan_block.booking.cancel_booking')}
              </span>
            </Button>
          </div>
        )
      }
    >
      {order && orderRealtyData && (
        <KyrgyzstanDetailContent
          realty={orderRealtyData}
          stayParams={{
            start_date: order.start_date,
            end_date: order.end_date,
            visitors_count: order.visitors_count,
            children_ages: order.children_ages,
          }}
          isShowVoucher={Boolean(order.file_url)}
          onOpenVoucher={handleOpenVoucher}
          childrenList={selectedChildren}
          childrenTitle={t('kyrgyzstan_block.detail.selected_room_title')}
          isChildrenListLoading={isChildrenListLoading}
          onOpenGallery={handleOpenGallery}
          onOpenMap={handleOpenFullMap}
        />
      )}
    </KyrgyzstanDetailShell>
  )
})

export default KyrgyzstanOrderDetailPage
